import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { serverAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';

interface FormData {
  asset_id: number;
  name: string;
  server_type: 'virtual' | 'physical';
  os: 'linux' | 'windows' | 'macos' | 'freebsd' | 'other';
  os_version: string;
  public_ip: string;
  private_ip: string;
  hostname: string;
  web_server_type: string;
  app_root_path: string;
}

interface RegistrationResult {
  id: number;
  server_id: string;
  name: string;
  install_command: string;
}

const ServerRegistrationPage: React.FC = () => {
  useDocumentTitle('Register Server');
  const navigate = useNavigate();
  const { assets, currentAsset } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResult | null>(null);

  const [formData, setFormData] = useState<FormData>({
    asset_id: currentAsset?.id || 0,
    name: '',
    server_type: 'virtual',
    os: 'linux',
    os_version: '',
    public_ip: '',
    private_ip: '',
    hostname: '',
    web_server_type: '',
    app_root_path: '/var/www/html',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (currentAsset) {
      setFormData(prev => ({ ...prev, asset_id: currentAsset.id }));
    }
  }, [currentAsset]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.asset_id) {
      newErrors.asset_id = 'Please select an asset';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Server name is required';
    }
    if (!formData.os) {
      newErrors.os = 'Operating system is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await serverAPI.create({
        asset_id: formData.asset_id,
        name: formData.name,
        server_type: formData.server_type,
        os: formData.os,
        os_version: formData.os_version || undefined,
        public_ip: formData.public_ip || undefined,
        private_ip: formData.private_ip || undefined,
        hostname: formData.hostname || undefined,
        web_server_type: formData.web_server_type as any || undefined,
        app_root_path: formData.app_root_path || undefined,
      });

      if (response.data.status === 'success') {
        setRegistrationResult(response.data.data);
        setStep(2);
        toast.success('Server registered successfully!');
      } else {
        toast.error(response.data.message || 'Failed to register server');
      }
    } catch (error: any) {
      console.error('Failed to register server:', error);
      toast.error(error.response?.data?.message || 'Failed to register server');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderStep1 = () => (
    <form onSubmit={handleSubmit}>
      <div className="row g-4">
        {/* Left Column - Server Details */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="mb-0">
                <i className="bi bi-server me-2 text-primary"></i>
                Server Details
              </h5>
            </div>
            <div className="card-body">
              {/* Asset Selection */}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  Select Asset <span className="text-danger">*</span>
                </label>
                <select
                  name="asset_id"
                  className={`form-select ${errors.asset_id ? 'is-invalid' : ''}`}
                  value={formData.asset_id}
                  onChange={handleChange}
                >
                  <option value="">Select an asset...</option>
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>{asset.name}</option>
                  ))}
                </select>
                {errors.asset_id && <div className="invalid-feedback">{errors.asset_id}</div>}
                <small className="text-muted">Associate this server with one of your assets</small>
              </div>

              {/* Server Name */}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  Server Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Production Web Server"
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              {/* Server Type and OS */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Server Type</label>
                  <select
                    name="server_type"
                    className="form-select"
                    value={formData.server_type}
                    onChange={handleChange}
                  >
                    <option value="virtual">Virtual (VPS/Cloud)</option>
                    <option value="physical">Physical (Dedicated)</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Operating System <span className="text-danger">*</span>
                  </label>
                  <select
                    name="os"
                    className={`form-select ${errors.os ? 'is-invalid' : ''}`}
                    value={formData.os}
                    onChange={handleChange}
                  >
                    <option value="linux">Linux</option>
                    <option value="windows" disabled>Windows (Coming Soon)</option>
                    <option value="macos">macOS</option>
                    <option value="freebsd">FreeBSD</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.os && <div className="invalid-feedback">{errors.os}</div>}
                </div>
              </div>

              {/* OS Version */}
              <div className="mb-4">
                <label className="form-label fw-semibold">OS Version</label>
                <input
                  type="text"
                  name="os_version"
                  className="form-control"
                  value={formData.os_version}
                  onChange={handleChange}
                  placeholder="e.g., Ubuntu 22.04 LTS"
                />
              </div>

              {/* Hostname */}
              <div className="mb-4">
                <label className="form-label fw-semibold">Hostname</label>
                <input
                  type="text"
                  name="hostname"
                  className="form-control"
                  value={formData.hostname}
                  onChange={handleChange}
                  placeholder="e.g., web-prod-01"
                />
              </div>

              {/* IP Addresses */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Public IP</label>
                  <input
                    type="text"
                    name="public_ip"
                    className="form-control"
                    value={formData.public_ip}
                    onChange={handleChange}
                    placeholder="e.g., 203.0.113.50"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Private IP</label>
                  <input
                    type="text"
                    name="private_ip"
                    className="form-control"
                    value={formData.private_ip}
                    onChange={handleChange}
                    placeholder="e.g., 10.0.1.50"
                  />
                </div>
              </div>

              {/* Web Server */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Web Server</label>
                  <select
                    name="web_server_type"
                    className="form-select"
                    value={formData.web_server_type}
                    onChange={handleChange}
                  >
                    <option value="">Select web server...</option>
                    <option value="nginx">Nginx</option>
                    <option value="apache">Apache</option>
                    <option value="nodejs">Node.js</option>
                    <option value="tomcat">Tomcat</option>
                    <option value="caddy">Caddy</option>
                    <option value="lighttpd">Lighttpd</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Application Root Path</label>
                  <input
                    type="text"
                    name="app_root_path"
                    className="form-control"
                    value={formData.app_root_path}
                    onChange={handleChange}
                    placeholder="/var/www/html"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Info */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h6 className="text-primary mb-3">
                <i className="bi bi-info-circle me-2"></i>
                What happens next?
              </h6>
              <ol className="ps-3 mb-0 small text-muted">
                <li className="mb-2">Register your server with basic details</li>
                <li className="mb-2">Get the agent installation command</li>
                <li className="mb-2">Run the command on your server</li>
                <li className="mb-2">Agent starts sending metrics automatically</li>
                <li>View real-time monitoring data</li>
              </ol>
            </div>
          </div>

          <div className="card border-0 bg-warning bg-opacity-10">
            <div className="card-body">
              <h6 className="text-warning mb-2">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Windows Support
              </h6>
              <p className="small mb-0 text-muted">
                Windows server monitoring is coming soon. Currently, only Linux-based servers are supported.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-4">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Registering...
            </>
          ) : (
            <>
              <i className="bi bi-arrow-right me-2"></i>
              Register Server
            </>
          )}
        </button>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <div className="row g-4">
      <div className="col-lg-8">
        {/* Success Message */}
        <div className="alert alert-success d-flex align-items-center mb-4">
          <i className="bi bi-check-circle-fill me-3 fs-4"></i>
          <div>
            <strong>Server registered successfully!</strong>
            <p className="mb-0 mt-1">Now install the CloudAyn Agent on your server to start monitoring.</p>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3">
            <h5 className="mb-0">
              <i className="bi bi-terminal me-2 text-primary"></i>
              Agent Installation - Linux
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <h6 className="text-muted mb-3">Step 1: SSH into your server</h6>
              <p className="text-muted small">Connect to your server via SSH:</p>
              <div className="bg-dark text-light p-3 rounded position-relative">
                <code>ssh root@{formData.public_ip || 'your-server-ip'}</code>
              </div>
            </div>

            <div className="mb-4">
              <h6 className="text-muted mb-3">Step 2: Run the installation command</h6>
              <p className="text-muted small">
                Copy and paste this command in your server terminal. This will download and install the CloudAyn Agent:
              </p>
              <div className="bg-dark text-light p-3 rounded position-relative">
                <pre className="mb-0 text-wrap" style={{ fontSize: '0.85rem' }}>
                  <code>{registrationResult?.install_command || 'Loading...'}</code>
                </pre>
                <button
                  className="btn btn-sm btn-outline-light position-absolute"
                  style={{ top: '8px', right: '8px' }}
                  onClick={() => copyToClipboard(registrationResult?.install_command || '')}
                >
                  <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'} me-1`}></i>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <h6 className="text-muted mb-3">Step 3: Verify installation</h6>
              <p className="text-muted small">Check if the agent is running:</p>
              <div className="bg-dark text-light p-3 rounded">
                <code>sudo systemctl status cloudayn-agent</code>
              </div>
            </div>

            <div className="mb-0">
              <h6 className="text-muted mb-3">Step 4: View your server</h6>
              <p className="text-muted small">
                Once the agent is installed, it will automatically start sending metrics.
                You can view your server's status and metrics in the dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Manual Installation */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-gear me-2 text-secondary"></i>
              Manual Installation (Advanced)
            </h5>
            <span className="badge bg-secondary">Optional</span>
          </div>
          <div className="card-body">
            <p className="text-muted small mb-3">
              If automatic installation doesn't work, you can manually install the agent:
            </p>

            <div className="accordion" id="manualInstall">
              <div className="accordion-item border-0">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed bg-light"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#debianInstall"
                  >
                    <i className="bi bi-ubuntu me-2"></i>
                    Debian/Ubuntu
                  </button>
                </h2>
                <div id="debianInstall" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                  <div className="accordion-body">
                    <div className="bg-dark text-light p-3 rounded small">
                      <pre className="mb-0">
{`# Download the agent
wget https://github.com/cloudayn/agent/releases/latest/download/cloudayn-agent-linux-amd64.deb

# Install the package
sudo dpkg -i cloudayn-agent-linux-amd64.deb

# Configure the agent
sudo nano /etc/cloudayn-agent/config.yaml

# Start the service
sudo systemctl enable cloudayn-agent
sudo systemctl start cloudayn-agent`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed bg-light"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#rhelInstall"
                  >
                    <i className="bi bi-box me-2"></i>
                    RHEL/CentOS/Fedora
                  </button>
                </h2>
                <div id="rhelInstall" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                  <div className="accordion-body">
                    <div className="bg-dark text-light p-3 rounded small">
                      <pre className="mb-0">
{`# Download the agent
curl -LO https://github.com/cloudayn/agent/releases/latest/download/cloudayn-agent-linux-amd64.rpm

# Install the package
sudo rpm -i cloudayn-agent-linux-amd64.rpm

# Configure and start
sudo systemctl enable cloudayn-agent
sudo systemctl start cloudayn-agent`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed bg-light"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#configFile"
                  >
                    <i className="bi bi-file-code me-2"></i>
                    Configuration File
                  </button>
                </h2>
                <div id="configFile" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                  <div className="accordion-body">
                    <p className="small text-muted">Create <code>/etc/cloudayn-agent/config.yaml</code>:</p>
                    <div className="bg-dark text-light p-3 rounded small">
                      <pre className="mb-0">
{`# CloudAyn Agent Configuration
api_endpoint: "${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/v1"
server_id: "${registrationResult?.server_id || 'SRV-xxxxxx'}"
api_key: "your_api_key"
api_secret: "your_api_secret"

# Metrics collection interval (seconds)
metrics_interval: 60

# Log collection settings
log_paths:
  - /var/log/syslog
  - /var/log/auth.log
  - /var/log/nginx/access.log
  - /var/log/nginx/error.log

# Web server type (nginx, apache, nodejs, etc.)
web_server: "${formData.web_server_type || 'nginx'}"

# Application root path
app_root: "${formData.app_root_path || '/var/www/html'}"`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="col-lg-4">
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-info-circle text-primary me-2"></i>
              Server Information
            </h6>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Server ID</td>
                  <td className="fw-medium">{registrationResult?.server_id}</td>
                </tr>
                <tr>
                  <td className="text-muted">Name</td>
                  <td className="fw-medium">{registrationResult?.name}</td>
                </tr>
                <tr>
                  <td className="text-muted">OS</td>
                  <td className="fw-medium">{formData.os} {formData.os_version}</td>
                </tr>
                {formData.hostname && (
                  <tr>
                    <td className="text-muted">Hostname</td>
                    <td className="fw-medium">{formData.hostname}</td>
                  </tr>
                )}
                {formData.public_ip && (
                  <tr>
                    <td className="text-muted">Public IP</td>
                    <td className="fw-medium">{formData.public_ip}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card border-0 bg-primary bg-opacity-10 mb-4">
          <div className="card-body">
            <h6 className="text-primary mb-2">
              <i className="bi bi-shield-check me-2"></i>
              Security Note
            </h6>
            <p className="small mb-0 text-muted">
              The agent only collects system metrics and logs. It does not have access to your application data or sensitive information.
            </p>
          </div>
        </div>

        <div className="d-grid gap-2">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/servers/${registrationResult?.id}`)}
          >
            <i className="bi bi-eye me-2"></i>
            View Server Dashboard
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate('/servers')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Servers
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <button
              className="btn btn-link text-muted p-0 mb-2"
              onClick={() => step === 2 ? setStep(1) : navigate('/servers')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              {step === 2 ? 'Back to form' : 'Back to servers'}
            </button>
            <h2 className="h3 mb-1">
              <i className="bi bi-plus-circle me-2 text-primary"></i>
              Register New Server
            </h2>
            <p className="text-muted mb-0">
              {step === 1
                ? 'Add a new server to monitor with CloudAyn Agent'
                : 'Follow the instructions to install the agent on your server'}
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center gap-3">
              <div className={`d-flex align-items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted'}`}>
                <span className={`badge rounded-pill ${step >= 1 ? 'bg-primary' : 'bg-secondary'}`}>1</span>
                <span className="fw-medium">Server Details</span>
              </div>
              <div className={`flex-grow-1 border-top ${step >= 2 ? 'border-primary' : ''}`}></div>
              <div className={`d-flex align-items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted'}`}>
                <span className={`badge rounded-pill ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`}>2</span>
                <span className="fw-medium">Install Agent</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {step === 1 ? renderStep1() : renderStep2()}
      </div>
    </DashboardLayout>
  );
};

export default ServerRegistrationPage;
